import mitt from 'mitt';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import angularMetadata from '../../assest/angular-metadata.json';
import reactMetadata from '../../assest/react-metadata.json';

export const emitter = mitt();

export interface CodeGenData {
  assets?: any;
  resultData?: any;
  configSelection?: any;
  codeSectionData?: CodeSectionType[];
}
export interface FilesAndFolderType {
  type: 'file' | 'folder';
  name: string;
  language?: string;
  extension?: string;
  content?: string;
  children?: FilesAndFolderType[];
  open?: boolean;
  refrenceId: string;
}

export interface CodeSectionType {
  prompt: string;
  promptRawResponse: string;
  filesWithFolders: FilesAndFolderType[];
  currentFileContent: { code: string; language: string };
  selectedFile?: FilesAndFolderType;
}

export const angularVersions = [
  {
    label: '15.x',
    value: '15.x',
  },
  {
    label: '16.x',
    value: '16.x',
  },
  {
    label: '17.x',
    value: '17.x',
  },
  {
    label: '18.x',
    value: '18.x',
  },
  {
    label: '19.x',
    value: '19.x',
  },
];
export const reactVersions = [
  {
    label: '18.x',
    value: '18.x',
  },
  {
    label: '19.x',
    value: '19.x',
  },
];

export const versions = {
  Angular: angularVersions,
  React: reactVersions,
};
export const styles = [
  {
    label: 'CSS',
    value: 'CSS',
  },
  {
    label: 'SCSS',
    value: 'SCSS',
  },
];
export const frameworksSupported = [
  {
    label: 'Angular',
    value: 'Angular',
  },
  {
    label: 'React',
    value: 'React',
  },
];

const getRegExp = (fileName: string) => {
  if (fileName.includes('.tsx')) {
    return /```tsx([\s\S]*?)```/g;
  }
  if (fileName.includes('.ts')) {
    return /```typescript([\s\S]*?)```/g;
  }
  if (fileName.includes('.html')) {
    return /```html([\s\S]*?)```/g;
  }
  if (fileName.includes('.css')) {
    return /```css([\s\S]*?)```/g;
  }
  if (fileName.includes('.scss')) {
    return /```scss([\s\S]*?)```/g;
  }
  return /```([\s\S]*?)```/g;
};

const isFile = (fileName: string) => {
  return (
    fileName &&
    (fileName.endsWith('.ts') ||
      fileName.endsWith('.tsx') ||
      fileName.endsWith('.html') ||
      fileName.endsWith('.css') ||
      fileName.endsWith('.scss') ||
      fileName.endsWith('.js') ||
      fileName.endsWith('.jsx') ||
      fileName.endsWith('.json') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.md') ||
      fileName.endsWith('.xml') ||
      fileName.endsWith('.yml') ||
      fileName.endsWith('.yaml') ||
      fileName.endsWith('.csv') ||
      fileName.endsWith('.sql') ||
      fileName.endsWith('.php') ||
      fileName.endsWith('.rb') ||
      fileName.endsWith('.go') ||
      fileName.endsWith('.swift') ||
      fileName.endsWith('.kt') ||
      fileName.endsWith('.java') ||
      fileName.endsWith('.c') ||
      fileName.endsWith('.cpp') ||
      fileName.endsWith('.cs'))
  );
};

export const extractFileNamesAndCode = (
  _code: any,
  callback?: Function,
  currentFileContent?: Function,
  isReact = false,
  filesWithFolders?: any[],
) => {
  let _filesWithFolders: any[] = filesWithFolders || [];
  const files = _code?.split(/\*\*File:\*\* /) || []; //_code?.split(/\*\*File:\*\* `(.+?)`/) || [];
  if (files.length > 1) {
    if (currentFileContent && files[0]) {
      currentFileContent({ code: files[0], language: 'text' });
    }
    for (let i = 1; i < files.length; i++) {
      const filePath =
        files[i]
          .match(/([\w\-./]+\/[\w\-]+(?:\.[\w\-]+)+)/g) //match(/([\w\-./]+\/[\w\-]+\.\w+)/)
          ?.[0]?.replace(/`/g, '') || ''; //files[i]?.split(' ')[0]?.replace(/`/g, '') || '';
      const content = files[i]
        ?.replace(`\'${filePath}\'`, '')
        ?.replace(filePath, '')
        ?.replace(getLanguage(filePath, true), '')
        ?.split('```')?.[1]
        //?.replace(/`/g, '')
        ?.trim();
      if (content?.length) {
        processFilePath(
          filePath, //files[i],
          getLanguage(filePath) || 'text',
          _filesWithFolders,
          content, //content?.[1] || '',
          currentFileContent,
        );
      }
    }
    if (callback) {
      callback(_filesWithFolders);
    }
  } else {
    if (currentFileContent && _code) {
      currentFileContent({ code: _code, language: 'text' });
    }
  }
};

const processFilePath = (
  filePath: string,
  language: string,
  path: any[],
  code: string,
  currentFileContent: Function | undefined,
): any[] => {
  filePath.split('/').forEach((each: string) => {
    if (isFile(each)) {
      const fileData = {
        ...getFileTemplate(),
        name: each,
        language,
        extension: each.endsWith('.spec.ts')
          ? 'spec'
          : each.endsWith('.spec.tsx') || each.endsWith('.test.tsx')
          ? 'test'
          : each.split('.').pop(),
        content: code,
      };
      if (currentFileContent) {
        currentFileContent({ code, language });
      }
      if (Array.isArray(path)) {
        path.push(fileData);
      }
    } else {
      const newFolder = {
        ...getFolderTemplate(),
        name: each,
        open: true,
      };
      if (Array.isArray(path)) {
        if (!path.length) {
          path.push(newFolder);
          path = path[0].children;
        } else {
          const found = path.find((each: any) => each.name === newFolder.name);
          if (!found) {
            path.push(newFolder);
            path = path[path.length - 1].children;
          } else {
            path = found?.children;
          }
        }
      }
      if (currentFileContent) {
        currentFileContent({ code: '', language: '' });
      }
    }
  });
  return path;
};

const getLanguage = (fileName: string, extension = false) => {
  if (fileName.includes('.tsx')) {
    return extension ? 'tsx' : 'typescript';
  }
  if (fileName.includes('.ts')) {
    return 'typescript';
  }
  if (fileName.includes('.html')) {
    return 'html';
  }
  if (fileName.includes('.css')) {
    return 'css';
  }
  if (fileName.includes('.scss')) {
    return 'scss';
  }
  if (fileName.includes('.json')) {
    return 'json';
  }
  if (fileName.includes('.jsx')) {
    return extension ? 'jsx' : 'javascript';
  }
  if (fileName.includes('.js')) {
    return 'javascript';
  }
  if (fileName.includes('.txt')) {
    return 'text';
  }
  if (fileName.includes('.md')) {
    return 'markdown';
  }
  if (fileName.includes('.xml')) {
    return 'xml';
  }
  if (fileName.includes('.yml')) {
    return 'yaml';
  }
  if (fileName.includes('.yaml')) {
    return 'yaml';
  }
  if (fileName.includes('.csv')) {
    return 'csv';
  }
  if (fileName.includes('.sql')) {
    return 'sql';
  }
  if (fileName.includes('.php')) {
    return 'php';
  }
  if (fileName.includes('.rb')) {
    return 'ruby';
  }
  if (fileName.includes('.go')) {
    return 'go';
  }
  if (fileName.includes('.swift')) {
    return 'swift';
  }
  if (fileName.includes('.kt')) {
    return 'kotlin';
  }
  if (fileName.includes('.java')) {
    return 'java';
  }
  if (fileName.includes('.cpp')) {
    return 'cpp';
  }
  if (fileName.includes('.cs')) {
    return 'csharp';
  }
  if (fileName.includes('.c')) {
    return 'c';
  }
  return '';
};
const formatCode = (code: any) => {
  return (
    code
      // Add line breaks after semicolons
      .replace(/;/g, ';\n')
      // Add line breaks around curly braces
      .replace(/\s*\{\s*/g, ' {\n')
      .replace(/\s*\}\s*/g, '\n}\n')
      // Add line breaks before HTML/JSX tags
      .replace(/</g, '\n<')
      // Add line breaks after closing tags
      .replace(/>/g, '>\n')
      // Remove multiple blank lines
      .replace(/\n{2,}/g, '\n')
      // Trim each line
      .split('\n')
      .map((line: any) => line.trim())
      .join('\n')
  );
};

export const getHTMLTemplate = (htmlCodeSinpnet: string, css: string = '') =>
  `
        <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8">
              <title>Preview</title>
              <style>
              @font-face {
                font-family: 'Calibre-R';
                font-style: normal;
                font-weight: 400;
                src: url('https://docs.emerald.cbre.com/assets/assets/fonts/calibre-r/calibre-r-web-regular.woff2') format('woff2');
                font-display: swap;
              }
              </style>
              <style>${css}</style>
            </head>
            <body>${htmlCodeSinpnet}</body>
          </html>
        `;

export const updateIFrameData = (
  iframeId: string,
  htmlCodeSinpnet: string,
  css: string = '',
) => {
  const preview_iFrame: HTMLIFrameElement = document.getElementById(
    iframeId,
  ) as HTMLIFrameElement;
  if (preview_iFrame) {
    preview_iFrame.srcdoc = getHTMLTemplate(htmlCodeSinpnet, css);
  }
};

export const getFileTemplate = (): FilesAndFolderType => {
  return {
    type: 'file',
    name: '',
    language: '',
    extension: '',
    content: '',
    refrenceId: 'id-' + Math.random().toString(36).substr(2, 9),
  };
};

export const getFolderTemplate = (): FilesAndFolderType => {
  return {
    type: 'folder',
    name: '',
    children: [],
    open: false,
    refrenceId: 'id-' + Math.random().toString(36).substr(2, 9),
  };
};

const updateFilesAndFolders = (children: any[], ref: JSZip | null) => {
  if (!ref) return null;
  children.forEach((child: any) => {
    if (child.type === 'folder') {
      const folder = ref.folder(child.name);
      if (child.children?.length) {
        updateFilesAndFolders(child.children, folder);
      }
    } else {
      ref.file(child.name, child.content);
    }
  });
  return ref;
};

export const includeFolderInFileStructure = (
  filesAndFolder: FilesAndFolderType[],
  toBeIncludedFolder: any,
  folderName: any,
): boolean | any[] => {
  if (
    !toBeIncludedFolder ||
    !folderName ||
    !Object.keys(toBeIncludedFolder).length ||
    !filesAndFolder?.length
  )
    return false;

  filesAndFolder.find((each: any) => {
    if (each.name === folderName) {
      each.children.push(toBeIncludedFolder);
      return true;
    } else if (each.children?.length) {
      return includeFolderInFileStructure(
        each.children,
        toBeIncludedFolder,
        folderName,
      );
    }
    return false;
  });
  return filesAndFolder;
};

export const downloadZip = async (folderStructure: any[]) => {
  const zip = new JSZip();

  if (!folderStructure || folderStructure.length === 0) return;
  const projectName: string = folderStructure[0].name;

  const rootFolder: JSZip | null = zip.folder(projectName);
  if (folderStructure[0].children?.length) {
    updateFilesAndFolders(folderStructure[0].children, rootFolder);
  }

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `${projectName}.zip`);
};

export const getEmeraldPrompt = (htmlData: string, framework: string) => {
  const metaData: any =
    framework === 'angular' ? angularMetadata : reactMetadata;
  const keys = Object.keys(metaData);
  const semanticElements = keys.filter(key => htmlData.includes(key));
  if (semanticElements.length === 0) {
    return { prompt: '', components: '' };
  }
  return {
    prompt:
      'Emerald components context provided below, use it for aplicable components generation:\n' +
      semanticElements
        .map(key => `${key}'s metadata: ${JSON.stringify(metaData[key])}`)
        .join('\n'),
    components: semanticElements.join(', '),
  };
};

export const getDynamicCode = (isNumbers = false) => {
  const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const characters = isNumbers ? numbers : letters;
  return Array.from(
    { length: isNumbers ? 6 : 10 },
    () => characters[Math.floor(Math.random() * characters.length)],
  ).join('');
};
